// --- ArtistProfileCard.js ---
// src/components/ArtistProfileCard.js
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  Card, CardContent, Typography, Box, Button, Divider, Modal,
  TextField, Stack, Alert, Select, InputLabel, OutlinedInput, Chip, MenuItem
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { http } from "../lib/axios";
import { EngagementSummary } from "../components/EngagementSummary";
import CollapsibleComments from "../components/CollapsibleComments";
import ProfileImageManager from './ProfileImageManager';
import { geocodeAddress } from '../lib/geocode';
import AgreementAcceptModal from "../components/AgreementAcceptModal";
import { getAgreementStatus } from "../lib/apiAgreements";
import { ART_TYPE_OPTIONS } from '../utils/hostProfileOptions';
import { useChat } from '../context/ChatContext';

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/media/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^https?:\/\/[^/]+/, '');
  }
  const trimmed = url.replace(/^\.?\/*/, '');
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

function formatCityStateZip(profile) {
  const city = profile?.city || profile?.location?.city || '';
  const state = profile?.state || profile?.location?.state || '';
  const zip = profile?.zip_code || profile?.zip || profile?.postal_code || profile?.location?.postal_code || '';

  if (city || state || zip) {
    const stateZip = [state, zip].filter(Boolean).join(' ');
    return [city, stateZip].filter(Boolean).join(', ');
  }

  const address = typeof profile?.address === 'string' ? profile.address : '';
  if (!address) return '';

  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return '';

  const stateZipRegex = /\b[A-Z]{2}\s+\d{5}(?:-\d{4})?\b/i;
  const stateZipIdx = parts.findIndex((part) => stateZipRegex.test(part));
  if (stateZipIdx > 0) {
    const stateZip = (parts[stateZipIdx].match(stateZipRegex) || [parts[stateZipIdx]])[0];
    return `${parts[stateZipIdx - 1]}, ${stateZip.toUpperCase()}`;
  }

  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  }

  return parts[0];
}

function ExpandableTextSection({ title, value, open, onToggle }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 0.5,
          borderBottom: '1px solid rgba(17,24,39,0.18)',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {open ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
        </Box>
      </Box>
      {open && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, whiteSpace: 'pre-line', lineHeight: 1.6 }}
        >
          {value || '–'}
        </Typography>
      )}
    </Box>
  );
}

export default function ArtistProfileCard({
  user,
  isSelf = false,
  // when a gallery host is viewing a represented artist
  canEditManaged = false,
  // optional callback for delete – otherwise use placeholder alert
  onDeleteArtist,
  onStartChat,
}) {
  const { launchChat } = useChat();
  const MAX_VISIBLE_SLIDER_DOTS = 12;
  const sliderRef = useRef(null);
  const canEdit = isSelf || canEditManaged;
  const isAuthenticated = !!localStorage.getItem('access_token');

  // This is the *accounts.User* id that owns this profile
  const profileOwnerUserId = user?.user?.id || user?.id || null;
  const chatTarget = useMemo(() => {
    if (!profileOwnerUserId) return null;
    return {
      id: profileOwnerUserId,
      user_id: profileOwnerUserId,
      display_name: user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      username: user?.username || user?.user?.username || '',
      email: user?.email || user?.user?.email || '',
      role: user?.role || user?.user?.role || 'artist',
      primary_profile_image: user?.primary_profile_image || null,
      profile_images: Array.isArray(user?.profile_images) ? user.profile_images : [],
    };
  }, [profileOwnerUserId, user]);
  const handleMessageClick = () => {
    const startChat = onStartChat || launchChat;
    if (startChat && chatTarget) startChat(chatTarget);
  };

  const [open, setOpen] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(null);
  const [agreementCheckError, setAgreementCheckError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    about: false,
    exhibitions: false,
    recognition: false,
  });

  // profile form fields
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    address: '',
    art_preferences: [],
    about: '',
    exhibitions: '',
    recognition: '',
    portfolio_link: ''
  });

  // profile images (for self or managed artist)
  const [profileImages, setProfileImages] = useState([]); // [{id, url, is_primary}]
  const [artistArtworks, setArtistArtworks] = useState([]);

  // generic fullscreen viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]); // [{src, caption}]
  const [viewerIndex, setViewerIndex] = useState(0);
  const [sliderDotIndex, setSliderDotIndex] = useState(0);

  const openViewerWithImages = (imgs, idx = 0) => {
    if (!Array.isArray(imgs) || !imgs.length) return;
    setViewerImages(imgs);
    setViewerIndex(idx);
    setViewerOpen(true);
  };
  const closeViewer = () => setViewerOpen(false);

  // hydrate form
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        address: user.address || '',
        art_preferences: user.art_preferences || [],
        about: user.about || user.bio || '',
        exhibitions: user.exhibitions || '',
        recognition: user.recognition || user.recognitions || '',
        portfolio_link: user.portfolio_link || ''
      });
    }
  }, [user]);

  useEffect(() => {
    setExpandedSections({
      about: false,
      exhibitions: false,
      recognition: false,
    });
  }, [user?.id]);

  useEffect(() => {
    if (!isSelf) return;
    let isActive = true;

    const checkAgreement = async () => {
      try {
        const res = await getAgreementStatus(["ARTIST_PLATFORM"]);
        const row = res.results?.find(item => item.code === "ARTIST_PLATFORM");
        if (isActive) {
          setAgreementAccepted(!!row?.accepted);
          setAgreementCheckError(null);
        }
      } catch (err) {
        console.error('Failed to check artist agreement status:', err);
        if (isActive) {
          setAgreementAccepted(false);
          setAgreementCheckError('Unable to verify artist agreement status.');
        }
      }
    };

    checkAgreement();

    return () => {
      isActive = false;
    };
  }, [isSelf, user?.id]);

  useEffect(() => {
    let active = true;
    if (!user?.id) {
      setArtistArtworks([]);
      return () => {
        active = false;
      };
    }

    http.get(`/artworks/?artist=${user.id}&view=card`)
      .then(({ data }) => {
        if (!active) return;
        setArtistArtworks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!active) return;
        setArtistArtworks([]);
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  // --- Profile Images (self or managed) ---

  // helper: base URL for profile-image operations
  const getProfileImageBaseUrl = () => {
    // If the artist is editing their *own* profile, we use the generic
    // "current user" endpoints like /api/accounts/profile-images/
    if (isSelf || !canEditManaged) {
      return '/api/accounts/profile-images/';
    }
    // Gallery host managing a represented artist -> user-specific endpoints
    if (profileOwnerUserId) {
      return `/api/accounts/users/${profileOwnerUserId}/profile-images/`;
    }
    return null;
  };

  const loadProfileImages = () => {
    const baseUrl = getProfileImageBaseUrl();
    if (!baseUrl) return;

    http.get(baseUrl)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const normalized = list.map(pi => ({
          ...pi,
          url: pi.url || pi.image || pi.image_url || null,
        }));
        setProfileImages(normalized);
      })
      .catch((e) => {
        console.warn('Failed to load profile images (artist)', e);
        setProfileImages([]);
      });
  };

  useEffect(() => {
    if (!canEdit) return;
    loadProfileImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, profileOwnerUserId]);

  useEffect(() => {
    if (!open || !canEdit) return;
    loadProfileImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canEdit]);

  const primaryImageUrl = useMemo(() => {
    const fromCard = user?.primary_profile_image_url
      ? toRelativeMedia(user.primary_profile_image_url)
      : null;
    if (fromCard) return fromCard;
    // From serializer (User.primary_profile_image)
    const fromUser = user?.primary_profile_image
      ? toRelativeMedia(user.primary_profile_image)
      : null;
    if (fromUser) return fromUser;

    if (!canEdit && Array.isArray(user?.profile_images) && user.profile_images.length) {
      const primary = user.profile_images.find((pi) => pi?.is_primary) || user.profile_images[0];
      const src = primary?.url || primary?.image || primary?.image_url;
      return src ? toRelativeMedia(src) : null;
    }

    // Fallback: primary from profileImages
    if (!profileImages?.length) return null;
    const primary = profileImages.find(pi => pi.is_primary);
    return primary?.url ? toRelativeMedia(primary.url) : null;
  }, [canEdit, user?.primary_profile_image_url, user?.primary_profile_image, user?.profile_images, profileImages]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChange = (e) =>
    setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleMultiChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: typeof value === 'string' ? value.split(',') : value }));
  };

  const handleSave = async () => {
    const token = localStorage.getItem('access_token');

    let locationPayload = null;

    // Geocode the address if present
    if (formData.address) {
      try {
        const data = await geocodeAddress(formData.address);
        const firstResult = data?.results?.[0];

        if (firstResult?.geometry) {
          const { lat, lng } = firstResult.geometry;
          locationPayload = {
            latitude: lat,
            longitude: lng,
          };
        }
      } catch (geoErr) {
        console.error('Failed to geocode artist address', geoErr);
      }
    }

    const payload = {
      user: {
        first_name: formData.first_name,
        last_name: formData.last_name,
      },
      address: formData.address,
      art_preferences: formData.art_preferences,
      about: formData.about,
      exhibitions: formData.exhibitions,
      recognition: formData.recognition,
      portfolio_link: formData.portfolio_link,
    };

    if (locationPayload) {
      payload.location = locationPayload;
    }

    http
      .put(
        `/artists/${user.id}/`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => handleClose())
      .catch((err) => console.error('Failed to update profile', err));
  };

  // Upload/makePrimary/delete — used when canEdit = true (self or managed)
  const onUpload = async (files) => {
    if (!files?.length || !canEdit) return;
    const baseUrl = getProfileImageBaseUrl();
    if (!baseUrl) return;

    const remaining = 3 - profileImages.length;
    const toSend = Array.from(files).slice(0, remaining);

    for (let i = 0; i < toSend.length; i++) {
      const fd = new FormData();
      fd.append('image', toSend[i]);
      if (!profileImages.some(p => p.is_primary) && i === 0) {
        fd.append('is_primary', 'true');
      }
      try {
        const { data } = await http.post(baseUrl, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const normalized = {
          ...data,
          url: data.url || data.image || data.image_url || null,
        };
        setProfileImages((prev) => [normalized, ...prev]);
      } catch (e) {
        console.error('Artist profile image upload failed', e);
      }
    }
  };

  const makePrimary = async (id) => {
    if (!canEdit) return;
    const baseUrl = getProfileImageBaseUrl();
    if (!baseUrl) return;

    try {
      const isManaged = canEditManaged && !isSelf;
      const url = isManaged
        ? `${baseUrl}${id}/set_primary/`
        : `/api/accounts/profile-images/${id}/make_primary/`;

      const { data } = await http.post(url);
      setProfileImages((prev) =>
        prev.map((p) => ({ ...p, is_primary: p.id === data.id }))
      );
    } catch (e) {
      console.error('Artist set primary failed', e);
    }
  };

  const deleteImage = async (id) => {
    if (!canEdit) return;
    const baseUrl = getProfileImageBaseUrl();
    if (!baseUrl) return;

    try {
      const isManaged = canEditManaged && !isSelf;
      const url = isManaged
        ? `${baseUrl}${id}/`
        : `/api/accounts/profile-images/${id}/`;

      await http.delete(url);
      setProfileImages((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error('Artist delete image failed', e);
    }
  };

  // For viewing only profile images in the manager / avatar click
  const profileViewerImages = useMemo(
    () =>
      (canEdit ? profileImages : (Array.isArray(user?.profile_images) ? user.profile_images : [])).map((pi) => ({
        src: toRelativeMedia(pi.url || pi.image || pi.image_url),
        caption: 'Profile',
      })),
    [canEdit, profileImages, user?.profile_images]
  );

  // Slider only for artwork images (no profile image)
  const sliderImages = useMemo(() => {
    const slides = [];
    for (const artwork of artistArtworks) {
      const title = artwork?.title || 'Artwork';
      const images = Array.isArray(artwork?.images) ? artwork.images : [];
      if (images.length) {
        images.forEach((img) => {
          const src = img?.url || img?.image || img?.image_url;
          if (src) slides.push({ src: toRelativeMedia(src), caption: title });
        });
      } else if (artwork?.primary_image_url) {
        slides.push({ src: toRelativeMedia(artwork.primary_image_url), caption: title });
      }
    }

    const seen = new Set();
    return slides.filter((img) => {
      if (!img?.src) return false;
      if (seen.has(img.src)) return false;
      seen.add(img.src);
      return true;
    });
  }, [artistArtworks]);

  const cityStateZip = useMemo(() => formatCityStateZip(user), [user]);
  const aboutText = user?.about || user?.bio || '';
  const exhibitionsText = user?.exhibitions || '';
  const recognitionText = user?.recognition || user?.recognitions || '';
  const artistPublicProfileHref = user?.id ? `/artists/${user.id}` : null;

  const sliderSettings = {
    dots: true,
    infinite: sliderImages.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: sliderImages.length > 1,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    pauseOnFocus: false,
    arrows: true,
    afterChange: (current) => setSliderDotIndex(current),
    appendDots: () => {
      const total = sliderImages.length;
      const visibleCount = Math.min(total, MAX_VISIBLE_SLIDER_DOTS);
      const start = Math.floor(sliderDotIndex / visibleCount) * visibleCount;
      const end = start + visibleCount;

      return (
        <Box
          sx={{
            mt: 1.5,
            px: 1,
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
            width: '100%',
            '& .slick-active button::before': {
              color: '#111827',
              opacity: 0.95,
            },
            '& li button::before': {
              color: 'rgba(17, 24, 39, 0.45)',
              opacity: 1,
            },
          }}
        >
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
              listStyle: 'none',
              maxWidth: '100%',
            }}
          >
            {Array.from({ length: end - start }, (_, offset) => {
              const index = start + offset;
              const isActive = index === sliderDotIndex;
              return (
                <Box
                  key={index}
                  component="li"
                  sx={{ width: 18, display: 'flex', justifyContent: 'center' }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Go to slide ${index + 1}`}
                    onClick={() => sliderRef.current?.slickGoTo(index)}
                    sx={{
                      width: isActive ? 12 : 9,
                      height: isActive ? 12 : 9,
                      p: 0,
                      border: 0,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      backgroundColor: isActive ? '#111827' : 'rgba(255, 255, 255, 0.18)',
                      boxShadow: isActive
                        ? '0 0 0 2px rgba(255, 255, 255, 0.9), 0 0 0 4px rgba(17, 24, 39, 0.2)'
                        : 'inset 0 0 0 1px rgba(17, 24, 39, 0.35)',
                      transition: 'background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease, width 160ms ease, height 160ms ease',
                      transform: isActive ? 'scale(1)' : 'scale(0.92)',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    },
  };

  const fullName = user?.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;

  return (
    <>
      <Card
        data-testid="artist-profile-card"
        sx={{
          width: { xs: '100%', sm: 360, md: 360, lg: 360 },
          maxWidth: { xs: '100%', sm: 360, md: 360, lg: 360 },
          height: 760,
          mb: 3,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          boxShadow: 10,
          overflow: 'hidden',
          backgroundColor: 'rgba(255, 255, 255, 0.6)', 
          backdropFilter: 'blur(16px)' 
        }}
      >
        {/* Slider: artist artwork images only */}
        {sliderImages.length > 0 ? (
          <Box
            sx={{
              p: 2,
              pb: 4,
              '& .slick-dots': {
                bottom: -28,
              },
            }}
          >
            <Slider ref={sliderRef} {...sliderSettings}>
              {sliderImages.map((img, idx) => (
                <Box
                  key={idx}
                  sx={{ height: 260, position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
                  onClick={() => openViewerWithImages(sliderImages, idx)}
                >
                  {/* blurred backdrop */}
                  <Box
                    component="img"
                    src={img.src}
                    alt=""
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(22px)',
                      transform: 'scale(1.1)',
                      opacity: 0.3,
                    }}
                  />
                  {/* main contained image */}
                  <Box
                    component="img"
                    src={img.src}
                    alt={img.caption || 'Image'}
                    sx={{
                      position: 'relative',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto',
                      height: '100%',
                    }}
                  />
                </Box>
              ))}
            </Slider>
          </Box>
        ) : (
          <Box sx={{ p: 2, pb: 4 }}>
            <Box
              sx={{
                height: 260,
                borderRadius: 2,
                border: '1px solid rgba(148,163,184,0.38)',
                background:
                  'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(226,232,240,0.9) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.55)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
                No artwork images yet
              </Typography>
            </Box>
          </Box>
        )}

        <CardContent
          sx={{
            pt: 1,
            pb: 3,
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              mt: 0,
              px: { xs: 1, md: 2 },
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
              {/* Primary profile image (self or other) */}
              {primaryImageUrl ? (
                <Box
                  component="img"
                  src={primaryImageUrl}
                  alt="Artist profile"
                  referrerPolicy="no-referrer"
                  onClick={() => {
                    if (profileViewerImages.length) {
                      const primaryIdx = profileImages.findIndex(pi => pi.is_primary);
                      const startIdx = primaryIdx >= 0 ? primaryIdx : 0;
                      openViewerWithImages(profileViewerImages, startIdx);
                    } else {
                      openViewerWithImages([{ src: primaryImageUrl, caption: 'Profile' }], 0);
                    }
                  }}
                  style={{
                    width: 88,
                    height: 88,
                    objectFit: 'cover',
                    borderRadius: '999px',
                    marginRight: 16,
                    border: '2px solid rgba(248,250,252,0.85)',
                    boxShadow: '0 10px 30px rgba(15,23,42,0.9)',
                    cursor: 'zoom-in',
                    backgroundColor: '#020617',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    mr: 2,
                    borderRadius: '50%',
                    border: '2px solid rgba(248,250,252,0.85)',
                    boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
                    background:
                      'linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(226,232,240,0.95) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                    No
                    <br />
                    Image
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography
                  data-testid="artist-profile-name"
                  variant="h5"
                  component={artistPublicProfileHref ? RouterLink : "h2"}
                  to={artistPublicProfileHref || undefined}
                  sx={{
                    fontWeight: 600,
                    color: "inherit",
                    textDecoration: artistPublicProfileHref ? "underline" : "none",
                    textDecorationColor: "rgba(17, 24, 39, 0.35)",
                    textUnderlineOffset: "0.14em",
                    "&:hover": artistPublicProfileHref
                      ? {
                          color: "primary.main",
                          textDecorationColor: "currentColor",
                        }
                      : undefined,
                  }}
                >
                  {fullName}
                </Typography>

                {user?.id && (
                  <Box sx={{ mt: 1 }}>
                    <EngagementSummary
                      target="artist"
                      objectId={user.id}
                      initialSummary={user.engagement_summary}
                    />
                  </Box>
                )}

                <Typography data-testid="artist-profile-email" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.role}
                </Typography>
                {isAuthenticated && !isSelf && chatTarget && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      mt: 1.25,
                      backgroundColor: '#e5e7eb',
                      color: '#111827',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#d1d5db',
                        boxShadow: 'none',
                      },
                    }}
                    onClick={handleMessageClick}
                  >
                    Message
                  </Button>
                )}
                {isSelf && agreementAccepted === false && (
                  <Box sx={{ mt: 1 }}>
                    <Alert
                      severity={agreementCheckError ? "error" : "warning"}
                      action={(
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowAgreementModal(true)}
                        >
                          Review
                        </Button>
                      )}
                    >
                      {agreementCheckError || "Accept the Artist Platform Agreement to upload artwork."}
                    </Alert>
                  </Box>
                )}
                <Typography data-testid="artist-profile-art-preferences" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Art Preferences:</strong>{' '}
                  {Array.isArray(user.art_preferences)
                    ? user.art_preferences.join(', ')
                    : user.art_preferences || '–'}
                </Typography>
              </Box>
            </Box>

          </Box>

          {/* Profile text, address, portfolio */}
          <Box sx={{ mt: 3, px: { xs: 0.5, md: 1 } }}>
            {cityStateZip && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <LocationOnIcon fontSize="small" color="action" sx={{ mr: 0.75 }} />
                <Typography variant="body2" color="text.secondary">
                  {cityStateZip}
                </Typography>
              </Box>
            )}

            <ExpandableTextSection
              title="About"
              value={aboutText}
              open={expandedSections.about}
              onToggle={() => setExpandedSections((prev) => ({ ...prev, about: !prev.about }))}
            />

            <ExpandableTextSection
              title="Exhibitions"
              value={exhibitionsText}
              open={expandedSections.exhibitions}
              onToggle={() => setExpandedSections((prev) => ({ ...prev, exhibitions: !prev.exhibitions }))}
            />

            <ExpandableTextSection
              title="Recognitions"
              value={recognitionText}
              open={expandedSections.recognition}
              onToggle={() => setExpandedSections((prev) => ({ ...prev, recognition: !prev.recognition }))}
            />

            {user.portfolio_link && (
              <Typography variant="body2" color="text.secondary">
                <strong>Portfolio:</strong>{' '}
                <a
                  href={user.portfolio_link}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {user.portfolio_link}
                </a>
              </Typography>
            )}

            {canEdit && (
              <Box sx={{ mt: 2.25, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  size="small"
                  onClick={handleOpen}
                >
                  Edit
                </Button>

                {canEditManaged && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={
                      onDeleteArtist ||
                      (() =>
                        alert(
                          'Delete Artist (placeholder) – gallery-delete backend support coming soon.'
                        ))
                    }
                  >
                    Delete
                  </Button>
                )}
              </Box>
            )}

            {user?.id && (
              <Box sx={{ mt: 2 }}>
                <CollapsibleComments target="artist" objectId={user.id} />
              </Box>
            )}
          </Box>

          <Divider sx={{ mt: 3 }} />
        </CardContent>
      </Card>

      {/* EDIT MODAL (self or managed) */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '50%', sm: '50%' },
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92vw', sm: 520 },
            bgcolor: 'background.paper',
            p: { xs: 2, sm: 3 },
            boxShadow: 24,
            borderRadius: 2,
            maxHeight: { xs: '90vh', sm: '85vh' },
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Typography variant="h6" mb={2}>Edit Profile</Typography>

          {canEdit && (
            <ProfileImageManager
              profileImages={profileImages}
              maxImages={3}
              onUpload={onUpload}
              onMakePrimary={makePrimary}
              onDelete={deleteImage}
              onThumbnailClick={(idx) => {
                if (!profileViewerImages.length) return;
                openViewerWithImages(profileViewerImages, idx);
              }}
            />
          )}

          <Stack spacing={2} mt={2}>
            <TextField label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} fullWidth />
            <TextField label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} fullWidth />
            <TextField label="Email" name="email" value={formData.email} onChange={handleChange} fullWidth disabled />
            <TextField label="Address" name="address" value={formData.address} onChange={handleChange} fullWidth />
            <Box>
              <InputLabel>Art Preferences</InputLabel>
              <Select
                multiple
                name="art_preferences"
                value={formData.art_preferences}
                onChange={handleMultiChange}
                input={<OutlinedInput fullWidth />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => (<Chip key={val} label={val} />))}
                  </Box>
                )}
                fullWidth
              >
                {ART_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </Box>
            <TextField label="About" name="about" value={formData.about} onChange={handleChange} fullWidth multiline rows={3} />
            <TextField label="Exhibitions" name="exhibitions" value={formData.exhibitions} onChange={handleChange} fullWidth multiline rows={3} />
            <TextField label="Recognitions" name="recognition" value={formData.recognition} onChange={handleChange} fullWidth multiline rows={3} />
            <TextField label="Portfolio Link" name="portfolio_link" value={formData.portfolio_link} onChange={handleChange} fullWidth />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleClose} sx={{ mr: 1 }}>Cancel</Button>
              <Button variant="contained" onClick={handleSave}>Save</Button>
            </Box>
          </Stack>
        </Box>
      </Modal>

      {isSelf && (
        <AgreementAcceptModal
          open={showAgreementModal}
          onClose={() => setShowAgreementModal(false)}
          agreementCode="ARTIST_PLATFORM"
          title="Artist Platform Agreement"
          workflowContext={{ workflow: "artist_profile", promo_code: "EARLYARTIST6_5PCT_PLATFORM" }}
          onAccepted={() => {
            setAgreementAccepted(true);
            setShowAgreementModal(false);
          }}
        />
      )}

      {/* Generic viewer */}
      <Modal open={viewerOpen} onClose={closeViewer}>
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Box sx={{ width: { xs: '95vw', md: '80vw' }, maxWidth: 1200 }}>
            <Slider dots arrows infinite initialSlide={viewerIndex} adaptiveHeight>
              {viewerImages.map((img, i) => (
                <Box key={i} sx={{ height: { xs: '70vh', md: '80vh' }, position: 'relative', overflow: 'hidden' }}>
                  <Box component="img" src={img.src} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(22px)', transform: 'scale(1.1)', opacity: 0.25 }} />
                  <Box component="img" src={img.src} alt={img.caption || 'Image'} sx={{ position: 'relative', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', m: '0 auto', height: '100%' }} />
                  {img.caption && (
                    <Typography variant="body2" align="center" sx={{ position: 'absolute', bottom: 12, left: 0, right: 0, color: '#fff', bgcolor: 'rgba(0,0,0,0.35)', px: 1, py: 0.5 }}>
                      {img.caption}
                    </Typography>
                  )}
                </Box>
              ))}
            </Slider>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button variant="outlined" onClick={closeViewer}>Close</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
